import type { BasicFacility } from '../../../js/api/models/BasicFacility';
import type { Facility } from '../../../js/api/models/Facility';
import { HealthcareEntitiesService } from '../../../js/api/services/HealthcareEntitiesService';

/**
 * Type representing a facility object that has city and state fields.
 * Used for location display calculation.
 */
type FacilityWithLocation = BasicFacility | Facility;

/**
 * Calculates the display value for a facility's location.
 * Returns "City, State" if both are present, otherwise returns city or state, or null if neither exists.
 */
function calculateLocationDisplayValue(
  facility: FacilityWithLocation | null | undefined
): string | null {
  if (!facility) {
    return null;
  }

  if (facility.city && facility.state) {
    return `${facility.city}, ${facility.state}`;
  }

  return facility.city || facility.state || null;
}

/**
 * Gets the location display value for a facility.
 * Can accept either a Facility/BasicFacility object (returns synchronously) or a facility ID (returns Promise).
 *
 * @param facility - A Facility or BasicFacility object (or null/undefined)
 * @returns The location display value (e.g., "City, State" or null)
 */
export function getFacilityLocationDisplayValue(
  facility: FacilityWithLocation | null | undefined
): string | null;

/**
 * Gets the location display value for a facility by ID.
 *
 * @param facilityId - A facility ID string
 * @returns A Promise that resolves to the location display value (e.g., "City, State" or null)
 */
export function getFacilityLocationDisplayValue(facilityId: string): Promise<string | null>;

export function getFacilityLocationDisplayValue(
  facilityOrId: FacilityWithLocation | string | null | undefined
): string | null | Promise<string | null> {
  // If it's already a Facility object (or null/undefined), calculate synchronously
  if (typeof facilityOrId !== 'string') {
    return calculateLocationDisplayValue(facilityOrId);
  }

  // Otherwise, it's a facility ID - fetch the facility first and return a Promise
  return HealthcareEntitiesService.healthcareEntitiesFacilitiesRetrieve({
    id: facilityOrId,
  })
    .then((facility) => calculateLocationDisplayValue(facility))
    .catch((error) => {
      // If fetching fails, return null
      console.error('Failed to fetch facility:', error);
      return null;
    });
}
